import { useRef, useState, useEffect } from "react";
import { Check, Wallpaper as WallpaperIcon, Upload, Trash2, Loader2, Maximize2, Minimize2, Move } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WallpaperRepositionDialog from "@/components/WallpaperRepositionDialog";
import {
  WALLPAPERS,
  getWallpaperId,
  setWallpaperId,
  getCustomWallpapers,
  addCustomWallpaper,
  removeCustomWallpaper,
  CUSTOM_WALLPAPER_LIMIT,
  getWallpaperFit,
  setWallpaperFit,
  WALLPAPER_FIT_EVENT,
} from "@/lib/wallpaper";

export default function WallpaperDialog({ open, onOpenChange }) {
  const [selected, setSelected] = useState(getWallpaperId());
  const [customs, setCustoms] = useState(getCustomWallpapers());
  const [uploading, setUploading] = useState(false);
  const [fit, setFit] = useState(getWallpaperFit());
  const [repositionOpen, setRepositionOpen] = useState(false);
  const fileRef = useRef(null);

  const activeCustom = customs.find((c) => c.id === selected);
  const isCustomActive = !!activeCustom;

  // Re-read every time the dialog opens so it reflects wallpapers picked
  // outside this component (e.g. from other tabs or preset scripts).
  useEffect(() => {
    if (!open) return;
    setSelected(getWallpaperId());
    setCustoms(getCustomWallpapers());
    setFit(getWallpaperFit());
  }, [open]);

  useEffect(() => {
    const onFit = () => setFit(getWallpaperFit());
    window.addEventListener(WALLPAPER_FIT_EVENT, onFit);
    return () => window.removeEventListener(WALLPAPER_FIT_EVENT, onFit);
  }, []);

  const pick = (id) => {
    setSelected(id);
    setWallpaperId(id);
  };

  const changeFit = (v) => {
    setFit(v);
    setWallpaperFit(v);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const entry = await addCustomWallpaper(f);
      setCustoms(getCustomWallpapers());
      pick(entry.id);
      toast.success("Wallpaper uploaded");
    } catch (err) {
      toast.error(err?.message || "Could not upload wallpaper");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCustom = (id, e) => {
    e.stopPropagation();
    removeCustomWallpaper(id);
    setCustoms(getCustomWallpapers());
    if (selected === id) setSelected(getWallpaperId());
  };

  const atLimit = customs.length >= CUSTOM_WALLPAPER_LIMIT;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wallpaper-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <WallpaperIcon className="h-5 w-5 text-[#3B82F6]" /> Chat wallpaper
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF] text-xs">
            Pick a preset or upload your own. Saved on this device.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 pt-1">
          {WALLPAPERS.map((w) => (
            <Swatch
              key={w.id}
              id={w.id}
              name={w.name}
              thumb={w.thumb}
              active={selected === w.id}
              onPick={() => pick(w.id)}
            />
          ))}
          {customs.map((c) => (
            <Swatch
              key={c.id}
              id={c.id}
              name="Custom"
              thumb={{ backgroundImage: `url("${c.url}")`, backgroundSize: "cover", backgroundPosition: "center" }}
              active={selected === c.id}
              onPick={() => pick(c.id)}
              onRemove={(e) => handleRemoveCustom(c.id, e)}
            />
          ))}

          <button
            type="button"
            data-testid="wallpaper-upload-btn"
            disabled={uploading || atLimit}
            onClick={() => fileRef.current?.click()}
            title={atLimit ? `Limit reached (${CUSTOM_WALLPAPER_LIMIT}). Remove one first.` : "Upload from device"}
            className="rounded-xl overflow-hidden border-2 border-dashed border-[#27272A] hover:border-[#3B82F6] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex flex-col items-center justify-center min-h-[6rem] p-2 gap-1"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 text-[#3B82F6] animate-spin" />
            ) : (
              <Upload className="h-5 w-5 text-[#9CA3AF]" />
            )}
            <span className="text-[10px] text-[#9CA3AF] text-center leading-tight">
              {uploading ? "Uploading…" : atLimit ? "Limit reached" : "Upload"}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="wallpaper-file-input"
            className="hidden"
            onChange={onPickFile}
          />
        </div>

        {isCustomActive && (
          <div className="mt-4 rounded-2xl bg-[#1E1E1E] border border-[#27272A] p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-medium text-[#F3F4F6]">Picture size</div>
                <div className="text-[10px] text-[#9CA3AF] leading-snug">
                  {fit === "cover"
                    ? "Fill fills the screen — parts of tall/wide photos may crop."
                    : "Fit shows the whole photo — edges are black if the shape doesn't match."}
                </div>
              </div>
              <div className="shrink-0 inline-flex rounded-full border border-[#27272A] bg-[#0A0A0A] p-0.5">
                <button
                  type="button"
                  data-testid="wallpaper-fit-contain"
                  onClick={() => changeFit("contain")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${
                    fit === "contain" ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-[#F3F4F6]"
                  }`}
                >
                  <Minimize2 className="h-3.5 w-3.5" /> Fit
                </button>
                <button
                  type="button"
                  data-testid="wallpaper-fit-cover"
                  onClick={() => changeFit("cover")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${
                    fit === "cover" ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-[#F3F4F6]"
                  }`}
                >
                  <Maximize2 className="h-3.5 w-3.5" /> Fill
                </button>
              </div>
            </div>

            <button
              type="button"
              data-testid="wallpaper-reposition-open"
              disabled={fit !== "cover"}
              onClick={() => setRepositionOpen(true)}
              title={fit === "cover" ? "Drag the photo to pick the visible part" : "Switch to Fill first — Fit already shows the whole photo"}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#0A0A0A] border border-[#27272A] hover:border-[#3B82F6] disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium text-[#F3F4F6] py-2.5 transition-all"
            >
              <Move className="h-3.5 w-3.5 text-[#3B82F6]" />
              Reposition photo
              {fit !== "cover" && (
                <span className="text-[10px] text-[#6B7280]">· needs Fill mode</span>
              )}
            </button>
          </div>
        )}

        <div className="text-[10px] text-[#6B7280] pt-2">
          Images are compressed (max 2400px wide) and stored only on this device. Max {CUSTOM_WALLPAPER_LIMIT} customs.
        </div>
      </DialogContent>
      <WallpaperRepositionDialog
        open={repositionOpen}
        onOpenChange={setRepositionOpen}
        wallpaper={activeCustom}
      />
    </Dialog>
  );
}

function Swatch({ id, name, thumb, active, onPick, onRemove }) {
  return (
    <button
      type="button"
      data-testid={`wallpaper-option-${id}`}
      onClick={onPick}
      className={`group relative rounded-xl overflow-hidden border transition-all ${
        active ? "border-[#3B82F6] ring-2 ring-[#3B82F6]/40" : "border-[#27272A] hover:border-[#3F3F46]"
      }`}
    >
      <div className="h-20 w-full" style={thumb} />
      <div className="px-2 py-1.5 bg-[#0A0A0A] text-xs text-[#F3F4F6] flex items-center justify-between">
        <span className="truncate">{name}</span>
        {active && <Check className="h-3.5 w-3.5 text-[#3B82F6] shrink-0" />}
      </div>
      {onRemove && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`wallpaper-remove-${id}`}
          onClick={onRemove}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRemove(e); } }}
          aria-label="Remove wallpaper"
          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/70 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-red-600 flex items-center justify-center text-white transition-opacity cursor-pointer"
        >
          <Trash2 className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}
